import { HeadersInit } from 'node-fetch';
import {
  HEADER_CHANNEL_INTEGRATION_ID,
  HEADER_BE_AUTHORIZATION,
  HEADER_BE_PASSWORD,
  HEADER_PRIMARY_CHANNEL_ID,
  HEADER_BE_USERNAME,
} from 'constants-activate';

import APP_URL from 'lane-shared/config/getAppUrl';
import { GetAuthTokenResponse } from 'application-types';
import { authorizationHeader } from 'lane-shared/helpers/formatters';

export const ENDPOINT = `${APP_URL}/api/v5/buildingEnginesPrism`;
export const UPLOAD_ENDPOINT = `${APP_URL}/api/v5/upload/buildingEnginesPrism`;
export const ADMIN_ENDPOINT = `${APP_URL}/api/v5/buildingEnginesPrismAdmin`;

export const SSO_LOGIN_ERROR_CODES = ['NIS', 'MAU', 'IUT'];

export type StringValues<T> = {
  [Property in keyof T]?: string;
};

interface Req<ParamsT, DataT> {
  path: string;
  method: 'get' | 'post' | 'delete' | 'put' | 'PATCH';
  token: string | null;
  params?: StringValues<ParamsT> | URLSearchParams | string;
  data?: DataT;
  channelIntegrationId?: string;
  email?: string;
  password?: string;
  channelId?: string;
  integrationId?: string;
  getAuthToken?: GetAuthTokenResponse;
  enableTokenManagement?: boolean;
}

export interface GetReq<ParamsT> extends Req<ParamsT, undefined> {
  method: 'get';
}

export interface DeleteReq<ParamsT> extends Req<ParamsT, undefined> {
  method: 'delete';
}

export interface PostReq<ParamsT, DataT extends object>
  extends Req<ParamsT, DataT> {
  method: 'post';
}

export interface PutReq<ParamsT, DataT extends object>
  extends Req<ParamsT, DataT> {
  method: 'put';
}

export interface PatchReq<ParamsT, DataT extends object>
  extends Req<ParamsT, DataT> {
  method: 'PATCH';
}

interface IRes<T> {
  status: number;
  data: Promise<T>;
}

export interface ResOk<T> extends IRes<T> {
  ok: true;
}

export interface ResErr<T> extends IRes<T> {
  ok: false;
}

// U = T only for compat, should remove
export type Res<T, U = T> = ResOk<T> | ResErr<U>;

export const constructUrl = ({
  endpoint = ENDPOINT,
  reqPath,
  params,
}: {
  endpoint?: string;
  reqPath: string;
  params?: URLSearchParams;
}): string => {
  let url = endpoint;

  url += reqPath.startsWith('/') ? reqPath : `/${reqPath}`;

  if (params && params.toString() !== '') url += `?${params.toString()}`;

  return url;
};

export async function request<ReqParamsT, ReqDataT extends object>(
  req:
    | GetReq<ReqParamsT>
    | PostReq<ReqParamsT, ReqDataT>
    | DeleteReq<ReqParamsT>
    | PutReq<ReqParamsT, ReqDataT>
    | PatchReq<ReqParamsT, ReqDataT>,
  opts: { raiseOnError: boolean } = { raiseOnError: true }
): Promise<Res<any, any>> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };

  if (req.getAuthToken && req.enableTokenManagement) {
    const authToken = await req.getAuthToken();

    headers['Authorization'] = authorizationHeader(authToken);
    headers['x-ssologin'] = 'true';
  } else if (req.token) {
    headers['X-BE-Authorization'] = `Bearer ${req.token}`;
  }

  if (req.channelIntegrationId) {
    headers[HEADER_CHANNEL_INTEGRATION_ID] = req.channelIntegrationId;
  }

  let params = new URLSearchParams();

  if (req.params instanceof URLSearchParams) {
    params = req.params;
  } else if (typeof req.params === 'string') {
    params = new URLSearchParams(req.params);
  } else if (typeof req.params === 'object') {
    for (const [key, value] of Object.entries(req.params)) {
      if (typeof value !== 'string')
        throw new Error(`TypeError: expected string got ${typeof value}`);

      params.set(key, value);
    }
  }

  const url = constructUrl({ reqPath: req.path, params });

  const res = await fetch(url, {
    method: req.method,
    headers,
    body: req.data && JSON.stringify(req.data),
  });

  if (opts.raiseOnError && !res.ok) {
    const data = await res.json();

    throw new Error(`${res.status.toString()}, ${{ cause: data }.toString()}`);
  }

  return {
    status: res.status,
    ok: res.ok,
    data: req.method === 'delete' ? Promise.resolve() : res.json(),
  };
}

export async function requestAdmin<ReqParamsT, ReqDataT extends object>(
  req:
    | GetReq<ReqParamsT>
    | PostReq<ReqParamsT, ReqDataT>
    | DeleteReq<ReqParamsT>
    | PutReq<ReqParamsT, ReqDataT>
    | PatchReq<ReqParamsT, ReqDataT>,
  opts: { raiseOnError: boolean } = { raiseOnError: true }
): Promise<Res<any, any>> {
  
  const headers = await getHeadersToSend(req);

  const url = constructUrl({
    reqPath: req.path,
    params: buildParams(req.params),
    endpoint: ADMIN_ENDPOINT,
  });

  const res = await fetch(url, {
    method: req.method,
    headers,
    body: req.data && JSON.stringify(req.data),
  });

  if (opts.raiseOnError && !res.ok) {
    const data = await res.json();

    throw new Error(`${res.status.toString()}, ${{ cause: data }.toString()}`);
  }

  return {
    status: res.status,
    ok: res.ok,
    data: req.method === 'delete' ? Promise.resolve() : res.json(),
  };
}

function buildParams(params: any): URLSearchParams {
  if (params instanceof URLSearchParams) return params;

  if (typeof params === 'string') return new URLSearchParams(params);

  const urlParams = new URLSearchParams();

  if (params && typeof params === 'object') {
    Object.entries(params).forEach(([key, value]) => {
      if (typeof value === 'string') {
        urlParams.set(key, value);
      } else {
        throw new Error(
          `Invalid parameter type: ${key} must be string, got ${typeof value}`
        );
      }
    });
  }

  return urlParams;
}

async function getHeadersToSend(req: any): Promise<Record<string, string>> {

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (req.channelIntegrationId) {
    headers[HEADER_CHANNEL_INTEGRATION_ID] = req.channelIntegrationId;
  }

  if (req.email && req.password) {
    headers[HEADER_BE_USERNAME] = req.email;
    headers[HEADER_BE_PASSWORD] = req.password;
  }

  if (req.channelId) {
    headers[HEADER_PRIMARY_CHANNEL_ID] = req.channelId;
  }

  if (req.getAuthToken && req.enableTokenManagement) {
    const authToken = await req.getAuthToken();

    headers['Authorization'] = authorizationHeader(authToken);
    headers['x-ssologin'] = 'true';
  } else if (req.token) {
    headers[HEADER_BE_AUTHORIZATION] = `Bearer ${req.token}`;
  }

  return headers;
}
